import { memo, useCallback, useEffect, useMemo } from 'react';
import { useAction, usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { useBox } from 'shared/hooks/utils';
import { useMap } from 'react-leaflet';
import { boundsNonEmpty, boundsToCenterAndZoom, calcGeoBounds, expandBounds, } from 'shared/map/lib/utils';
import { setMapCenter, setMapCenterAndZoom } from 'shared/map/model/slice';
import { async_select_tracking_gps_and_icons, device_last_loc_selector, select_confirmed_viewport, select_loading, } from 'entities/device.map.track/lib/selectors';
import { useAPI } from 'console/shared/api/api-context';
import { debounce } from 'throttle-debounce';
const nullable_viewport = [0, 0];
export const ZoomToTrack = memo(function ZoomToTrack(props) {
    const { devices, device_id } = props;
    const boxedDevices = useBox(devices);
    const initially_positioned = useBox(false);
    const onSetMapCenterAndZoom = useAction(setMapCenterAndZoom);
    const map = useMap();
    const onResize = useCallback(() => {
        // if (container == null) {
        //     return;
        // }
        if (initially_positioned.current) {
            return;
        }
        const xy = boxedDevices.current
            .map((dev) => dev.last_gps?.[0])
            .filter((last_gps) => last_gps != null)
            .map((last_gps) => [last_gps.x, last_gps.y]);
        if (xy.length === 0) {
            return;
        }
        const x = xy.map(([x]) => x);
        const y = xy.map(([, y]) => y);
        const strict_bounds = [
            [Math.min(...x), Math.min(...y)],
            [Math.max(...x), Math.max(...y)],
        ];
        const bounds_with_gap = expandBounds(strict_bounds, 0.1);
        if (bounds_with_gap == null) {
            return;
        }
        const loc_bounds = [
            [bounds_with_gap[0][0], bounds_with_gap[0][1]],
            [bounds_with_gap[1][0], bounds_with_gap[1][1]],
        ];
        const result = boundsToCenterAndZoom(loc_bounds, [
            map.getContainer().offsetWidth,
            map.getContainer().offsetHeight,
        ]);
        if (result != null) {
            const [center, zoom] = result;
            initially_positioned.current = true;
            onSetMapCenterAndZoom({ center, zoom: Math.min(zoom, 13), device_id });
        }
    }, [device_id]);
    //Центрируется на машинку при запуске
    useEffect(() => {
        onResize();
    }, []);
    // изменили на графике скорости временной интервал
    const confirmed_viewport = usePartialAppSelector(select_confirmed_viewport, device_id) ??
        nullable_viewport;
    const api = useAPI();
    const gps_and_events = usePartialAppSelector(async_select_tracking_gps_and_icons, device_id, api);
    const device_last_loc = usePartialAppSelector(device_last_loc_selector, device_id);
    const loading = usePartialAppSelector(select_loading, device_id);
    const onWantMapCenter = useAction(setMapCenter);
    useEffect(() => {
        gps_and_events != null &&
            gps_and_events.then(
            // @ts-ignore
            ([gps, _]) => {
                if (gps != null && confirmed_viewport != null) {
                    const bounds = calcGeoBounds(gps, confirmed_viewport);
                    if (boundsNonEmpty(bounds)) {
                        debouncedOnWantBounds(expandBounds(bounds, 0.05));
                    }
                    else if (loading && device_last_loc != null) {
                        onWantMapCenter({ center: device_last_loc, device_id });
                    }
                }
            });
    }, [confirmed_viewport, device_id]);
    const requestBounds = useCallback((bounds) => {
        const map_size = map.getSize();
        if (!map_size) {
            return;
        }
        if (map_size.x === 0 && map_size.y === 0) {
            return;
        }
        const result = boundsToCenterAndZoom(bounds, [
            map.getContainer().offsetWidth,
            map.getContainer().offsetHeight,
        ]);
        if (result != null) {
            const [center, zoom] = result;
            if (zoom === -Infinity || zoom === Infinity) {
                return;
            }
            onSetMapCenterAndZoom({ center, zoom: Math.min(17, zoom), device_id });
        }
    }, [device_id]);
    const debouncedOnWantBounds = useMemo(() => debounce(300, requestBounds), [requestBounds]);
    return null;
});
